import { DriveItem } from '@microsoft/microsoft-graph-types';
import { FC, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FiAlertTriangle } from 'react-icons/fi';
import { Img } from 'react-suspense-img';
import { useItemThumbnail } from '../../services/graph/hooks/documentHelper';
import { Skeleton } from '../loading/Skeleton';
import { DocumentCategory } from './DocumentCategory';
import { DocumentDeleteButton } from './DocumentDeleteButton';
import { DocumentDetails } from './DocumentDetails';
import { DocumentDownloadButton } from './DocumentDownloadButton';
import { DocumentUpdateButton } from './DocumentUpdateButton';

interface DocumentPreviewProps {
  file: DriveItem;
  showCategory?: boolean;
  onDelete?: () => void;
  onUpdate?: () => void;
}

const ErrorLoadingThumbnail = () => {
  return (
    <div className="bg-white px-8 py-4 text-center flex items-center justify-center flex-col gap-2">
      <FiAlertTriangle className="h-10 w-10" />
      <div className="text-primary-std">Error loading thumbnail</div>
    </div>
  );
};

const DocumentPreviewContent: FC<DocumentPreviewProps> = ({ file, showCategory, onDelete, onUpdate }) => {
  const { thumbnailUrl, refetch: refetchThumbnail } = useItemThumbnail(file.id!);

  const updateListAndThumbnail = () => {
    onUpdate && onUpdate();
    refetchThumbnail();
  };

  return (
    <div>
      <div className="rounded-md overflow-hidden border border-primary-std">
        {showCategory && <DocumentCategory file={file} />}
        <DocumentDetails file={file} />
        <ErrorBoundary fallback={<ErrorLoadingThumbnail />}>
          {thumbnailUrl && <Img className="w-full" src={thumbnailUrl} />}
        </ErrorBoundary>
        <DocumentDownloadButton file={file} />
        {onUpdate && <DocumentUpdateButton file={file} onUpdate={updateListAndThumbnail} />}
        {onDelete && <DocumentDeleteButton file={file} onDelete={onDelete} />}
      </div>
    </div>
  );
};

export const DocumentPreview: FC<DocumentPreviewProps> = props => {
  return (
    <Suspense fallback={<Skeleton className="h-[400px]" />}>
      <DocumentPreviewContent {...props} />
    </Suspense>
  );
};
